import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  estimateVisit,
  FACILITY_LABELS,
  FREQUENCY_TIERS,
  VISIT_MINIMUM,
  SALES_TAX,
} from '@shared/pricing';
import type { FacilityType, Frequency } from '@shared/types';
import { FACILITY_TYPES } from '@shared/types';
import { formatCurrency, formatSqft } from '@shared/utils';

/**
 * Instant estimate.
 *
 * Deliberately not a lead-gate: the number appears without an email address. The
 * research is blunt that 54% of B2B buyers are less likely to buy without a price and
 * 16% cross a vendor off entirely — so the cost of hiding it is higher than the value
 * of the email. The CTA under the figure is where the lead comes from.
 */
export function Estimator() {
  const [facility, setFacility] = useState<FacilityType>('office');
  const [frequency, setFrequency] = useState<Frequency>('3x');
  const [sqft, setSqft] = useState(5000);

  const estimate = useMemo(
    () => estimateVisit(facility, frequency, sqft),
    [facility, frequency, sqft],
  );

  const tier = FREQUENCY_TIERS.find((t) => t.key === frequency);

  return (
    <div className="estimator">
      <div className="estimator-controls">
        <div className="field">
          <label htmlFor="est-facility">What kind of space?</label>
          <select
            id="est-facility"
            value={facility}
            onChange={(e) => setFacility(e.target.value as FacilityType)}
          >
            {FACILITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {FACILITY_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="est-frequency">How often?</label>
          <select
            id="est-frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as Frequency)}
          >
            {FREQUENCY_TIERS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label} — {t.cadence}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <div className="range-row">
            <label htmlFor="est-sqft">How big, roughly?</label>
            <span className="range-value">{formatSqft(sqft)} sq ft</span>
          </div>
          <input
            id="est-sqft"
            type="range"
            min={500}
            max={60000}
            step={500}
            value={sqft}
            onChange={(e) => setSqft(Number(e.target.value))}
          />
          <p className="hint">Drag it. A guess within a thousand feet is close enough.</p>
        </div>
      </div>

      <div className="estimator-result">
        {estimate ? (
          <>
            <p className="estimator-figure">{formatCurrency(estimate.perVisit)} per visit</p>
            <p className="estimator-sub">
              About {formatCurrency(estimate.perMonth)} a month at {tier?.cadence.toLowerCase()}.
              {estimate.minimumApplied
                ? ` That is our ${formatCurrency(VISIT_MINIMUM)} visit minimum — the space is small enough that the minimum sets the price.`
                : ` That is $${estimate.ratePerSqft.toFixed(2)} per square foot.`}
            </p>
            <p className="estimator-sub">
              Before Florida sales tax, which is{' '}
              {(SALES_TAX.counties[0].rate * 100).toFixed(1)}% in {SALES_TAX.counties[0].county} and{' '}
              {(SALES_TAX.counties[1].rate * 100).toFixed(1)}% in {SALES_TAX.counties[1].county}.
            </p>
          </>
        ) : (
          <>
            <p className="estimator-figure">Let&rsquo;s talk it through</p>
            <p className="estimator-sub">
              We do not have a published rate for that kind of space, and guessing at one would not
              help you. Twenty minutes on site gets you a real number.
            </p>
          </>
        )}

        <div className="btn-row" style={{ marginTop: '0.5rem' }}>
          <Link to="/quote" className="btn btn-primary">
            Book a 20-minute walkthrough
          </Link>
        </div>
      </div>
    </div>
  );
}
